import type { EChartsOption } from 'echarts';
import { ChartContext } from './chart.models';

export function buildChartOptions(context: ChartContext, reducedMotion = false): EChartsOption {
  if (context.type === 'tree') return buildTreeOptions(context, reducedMotion);
  if (context.type === 'heatmap') return buildHeatmapOptions(context, reducedMotion);
  const darkCartesian = (context.type === 'line' || context.type === 'bar') && context.theme === 'dark';
  const axisColor = darkCartesian ? '#91abc4' : '#1b2430';
  return {
    color: context.colors || [
      'rgb(111, 255, 111)',
      'rgb(255, 153, 204)',
      'rgb(29, 179, 214)',
      'rgb(45, 103, 103)',
      'rgb(251, 101, 150)',
      'rgb(36, 123, 46)',
      'rgb(103, 13, 183)',
    ],
    textStyle: { fontFamily: 'Vazirmatn, vazir, Tahoma, sans-serif' },
    aria: {
      enabled: true,
      ...(context.type === 'line' || context.type === 'bar' ? {
        label: {
          description: `${context.title}. ${context.data.map(item => `${item.name}: ${item.value === null ? 'داده موجود نیست' : item.value.toLocaleString('en-US', { maximumFractionDigits: 2 }) + (context.unit ? ' ' + context.unit : '')}`).join('؛ ')}`,
        },
      } : {}),
    },
    tooltip: {
      show: context.type !== 'ring-gauge',
      trigger: context.type === 'line' ? 'axis' : 'item',
      ...(context.type === 'line' || context.type === 'bar' ? {
        valueFormatter: (value: unknown) => typeof value === 'number'
          ? `${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}${context.unit ? ' ' + context.unit : ''}` : '—',
        backgroundColor: darkCartesian ? '#10283e' : '#fff',
        borderColor: darkCartesian ? '#1b3954' : '#ddd',
      } : {}),
      renderMode: 'html',
      className: 'app-chart-tooltip',
      confine: true,
      textStyle: { fontFamily: 'Vazirmatn, vazir, Tahoma, sans-serif', color: axisColor },
      extraCssText: 'direction:rtl;text-align:right;max-width:calc(100% - 16px);white-space:normal;overflow-wrap:anywhere;',
    },
    legend: {
      show: context.type === 'pie' || context.type === 'doughnut',
      type: 'scroll',
      top: 0,
      left: 'center',
      width: '95%',
      textStyle: { color: '#1b2430', fontSize: 12 },
    },
    ...(context.type === 'line' ? {
      grid: { left: 12, right: 48, top: 30, bottom: context.zoom ? 52 : 12, containLabel: true },
      xAxis: {
        type: 'category' as const,
        boundaryGap: false,
        data: context.data.map(item => item.name),
        axisLabel: { color: axisColor, hideOverlap: true },
        axisLine: { lineStyle: { color: darkCartesian ? '#1b3954' : '#ddd' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value' as const,
        scale: true,
        min: context.min,
        name: context.unit,
        nameTextStyle: { color: axisColor },
        axisLabel: { color: axisColor },
        splitLine: { lineStyle: { color: darkCartesian ? '#1b3954' : '#eee', type: 'dashed' as const } },
      },
    } : context.type === 'bar' ? {
      grid: { left: 12, right: 16, top: context.showValues || context.unit ? 30 : 16, bottom: context.zoom ? 52 : 12, containLabel: true },
      xAxis: {
        type: 'category' as const,
        data: context.data.map(item => item.name),
        axisLabel: { color: axisColor, width: 100, overflow: 'truncate' as const, hideOverlap: true },
        ...(darkCartesian ? {
          axisLine: { lineStyle: { color: '#1b3954' } },
          axisTick: { show: false },
        } : {}),
      },
      yAxis: {
        type: 'value' as const, min: 0, minInterval: 1,
        ...(context.unit ? { name: context.unit, nameTextStyle: { color: axisColor } } : {}),
        ...(context.max !== undefined ? { max: context.max } : {}),
        ...(darkCartesian ? {
          axisLabel: { color: axisColor },
          splitLine: { lineStyle: { color: '#1b3954', type: 'dashed' as const } },
        } : {}),
      },
    } : {}),
    ...((context.type === 'line' || context.type === 'bar') ? {
      dataZoom: context.zoom ? [
        { type: 'inside' as const, xAxisIndex: 0, moveOnMouseMove: false, moveOnMouseWheel: false },
        { type: 'slider' as const, xAxisIndex: 0, bottom: 4, height: 24, showDetail: false },
      ] : [],
    } : {}),
    series: context.type === 'line' ? [{
      name: context.title,
      type: 'line',
      connectNulls: false,
      symbol: 'circle',
      symbolSize: 9,
      itemStyle: {
        borderColor: darkCartesian ? '#092035' : '#fff',
        borderWidth: 2,
      },
      lineStyle: { width: 3 },
      emphasis: { scale: 1.5 },
      animation: !reducedMotion,
      animationDuration: 1200,
      animationDurationUpdate: 800,
      animationEasing: 'cubicOut',
      data: context.data,
    }] : context.type === 'ring-gauge' ? [{
      name: context.title,
      type: 'gauge',
      startAngle: 90,
      endAngle: -270,
      center: ['50%', '50%'],
      radius: '89%',
      min: 0,
      max: 100,
      axisLine: { lineStyle: { width: 12, color: [[1, context.colors?.[1] || '#204059']] } },
      progress: {
        show: (context.data[0]?.value ?? 0) > 0,
        roundCap: true,
        width: 12,
        itemStyle: {
          color: context.colors?.[0] || '#3adab5',
        },
      },
      emphasis: {
        itemStyle: {
          shadowColor: context.colors?.[0] || '#3adab5',
          shadowBlur: 16,
        },
      },
      pointer: { show: false },
      anchor: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      title: { show: false },
      detail: {
        valueAnimation: !reducedMotion,
        offsetCenter: [0, '-8%'],
        color: '#e7f1ff',
        fontSize: 32,
        fontWeight: 800,
        formatter: (value: number) => `${value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`,
      },
      animation: !reducedMotion,
      animationDuration: 1600,
      animationDurationUpdate: 1000,
      animationEasing: 'cubicOut',
      animationEasingUpdate: 'cubicOut',
      data: context.data.slice(0, 1),
    }] : context.type === 'bar' ? [{
      name: context.title,
      type: 'bar',
      colorBy: 'data',
      ...(darkCartesian ? { itemStyle: { borderRadius: [4, 4, 0, 0] } } : {}),
      ...(context.showValues ? {
        barMaxWidth: 83,
        itemStyle: { borderRadius: [7, 7, 0, 0] },
        label: {
          show: true,
          position: 'top' as const,
          color: darkCartesian ? '#edf7ff' : '#1b2430',
          formatter: (params: { value: unknown }) => typeof params.value === 'number'
            ? `${params.value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}${context.unit || ''}` : '—',
        },
      } : {}),
      animation: !reducedMotion,
      animationDuration: 1000,
      animationEasing: 'cubicOut',
      data: context.data,
    }] : context.type === 'gauge' ? [{
      name: context.title,
      type: 'gauge',
      startAngle: 180,
      endAngle: 0,
      center: ['50%', '94%'],
      radius: '180%',
      min: 0,
      max: context.max > 0 ? context.max : 1,
      axisLine: {
        lineStyle: {
          width: 50,
          color: [
            [1 / 3, 'rgba(255, 99, 132, 0.2)'],
            [2 / 3, 'rgba(255, 206, 86, 0.2)'],
            [1, 'rgba(63, 191, 63, 0.2)'],
          ],
        },
      },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      pointer: { length: '95%', width: 4, itemStyle: { color: '#000' } },
      anchor: { show: true, size: 10, itemStyle: { color: '#000' } },
      title: { show: false },
      detail: { show: false },
      animation: true,
      animationDuration: 1000,
      animationDurationUpdate: 1000,
      animationEasing: 'cubicOut',
      animationEasingUpdate: 'cubicOut',
      data: context.data,
    }] : [{
      name: context.title,
      type: 'pie',
      animation: true,
      animationType: 'scale',
      animationDuration: 1000,
      animationEasing: 'cubicOut',
      radius: context.type === 'doughnut' ? ['40%', '65%'] : '65%',
      center: ['50%', '58%'],
      stillShowZeroSum: false,
      label: { show: false },
      data: context.data,
    }],
  };
}

function buildHeatmapOptions(context: Extract<ChartContext, { type: 'heatmap' }>, reducedMotion: boolean): EChartsOption {
  const dark = context.theme === 'dark';
  const columns = Math.max(1, Math.min(context.columns || 15, context.data.length || 1));
  const rows = Math.max(1, Math.ceil(context.data.length / columns));
  const max = Math.max(1, ...context.data.filter(item => item.value !== null && Number.isFinite(item.value)).map(item => item.value));
  const formatValue = (value: number | null) => value === null ? 'داده موجود نیست'
    : `${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${context.unit ? ' ' + context.unit : ''}`;
  const escapeHtml = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return {
    textStyle: { fontFamily: 'Vazirmatn, vazir, Tahoma, sans-serif' },
    aria: {
      enabled: true,
      label: { description: `${context.title}. ${context.data.map(item => `${item.name}: ${formatValue(item.value)}؛ ${(item.details || []).join('؛ ')}`).join('؛ ')}` },
    },
    grid: { left: 0, right: 0, top: 15, bottom: 15 },
    xAxis: {
      type: 'category',
      data: Array.from({ length: columns }, (_, index) => index),
      show: false,
    },
    yAxis: { type: 'category', data: Array.from({ length: rows }, (_, index) => index), inverse: true, show: false },
    visualMap: {
      show: false,
      min: 0,
      max,
      dimension: 2,
      inRange: { color: context.colors || ['#15394a', '#28d2b6'] },
    },
    tooltip: {
      trigger: 'item',
      renderMode: 'html',
      className: 'app-chart-tooltip',
      confine: true,
      padding: [8, 12],
      backgroundColor: dark ? '#10283e' : '#fff',
      borderColor: dark ? '#1b3954' : '#ddd',
      textStyle: { color: dark ? '#edf7ff' : '#1b2430', fontFamily: 'Vazirmatn, vazir, Tahoma, sans-serif', fontSize: 12 },
      extraCssText: 'direction:rtl;text-align:right;line-height:1.9;max-width:220px;white-space:normal;overflow-wrap:anywhere;border-radius:8px;',
      formatter: params => {
        const item = context.data[(Array.isArray(params) ? params[0] : params).dataIndex];
        if (!item) return '';
        const value = item.value === null ? 'داده موجود نیست'
          : `<bdi>${item.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</bdi> ${escapeHtml(context.unit || '')}`;
        return `<bdi>${escapeHtml(item.name)}</bdi><br>${value}${item.warning ? ' · نیازمند بررسی' : ''}`;
      },
    },
    series: [{
      name: context.title,
      type: 'custom',
      renderItem: (params, api) => {
        const point = api.coord([api.value(0), api.value(1)]);
        const size = api.size([1, 1]) as number[];
        const gap = api.getWidth() < 360 ? 3 : 5;
        const item = context.data[params.dataIndex];
        return {
          type: 'rect',
          shape: {
            x: point[0] - size[0] / 2 + gap / 2,
            y: point[1] - size[1] / 2 + gap / 2,
            width: Math.max(1, size[0] - gap),
            height: Math.max(1, size[1] - gap),
            r: 5,
          },
          style: {
            fill: item.value === null ? (dark ? 'rgba(57,217,178,0.12)' : '#e2e8ef') : api.visual('color') as string,
            stroke: item.warning ? '#ffc16c' : dark ? '#ffffff13' : '#00000013',
            lineWidth: 1,
          },
        };
      },
      data: context.data.map((item, index) => ({
        name: item.name,
        value: [index % columns, Math.floor(index / columns), item.value ?? 0],
        ...(item.value === null ? {
          visualMap: false,
        } : {}),
      })),
      animation: !reducedMotion,
      animationDuration: 700,
    }],
  };
}

function buildTreeOptions(context: Extract<ChartContext, { type: 'tree' }>, reducedMotion: boolean): EChartsOption {
  return {
    textStyle: { fontFamily: 'Vazirmatn, vazir, Tahoma, sans-serif' },
    aria: { enabled: true },
    tooltip: {
      trigger: 'item',
      triggerOn: 'mousemove|click',
      confine: true,
      renderMode: 'html',
      className: 'app-chart-tooltip',
      extraCssText: 'direction:rtl;text-align:right;max-width:calc(100% - 16px);white-space:normal;overflow-wrap:anywhere;',
      textStyle: { fontFamily: 'Vazirmatn, vazir, Tahoma, sans-serif' },
      formatter: params => {
        const item = Array.isArray(params) ? params[0] : params;
        return typeof item.value === 'number'
          ? 'تعداد ' + item.value.toLocaleString('fa-IR') + ' زیر مجموعه'
          : item.name.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      },
    },
    series: [{
      name: context.title,
      type: 'tree',
      data: context.data,
      top: 24, left: '15%', bottom: 24, right: '22%',
      symbolSize: 10,
      initialTreeDepth: 1,
      label: { position: 'left', align: 'right', verticalAlign: 'middle', fontSize: 12, color: '#1b2430' },
      leaves: { label: { position: 'right', align: 'left', verticalAlign: 'middle' } },
      expandAndCollapse: true,
      roam: true,
      animation: !reducedMotion,
      animationDuration: 550,
      animationDurationUpdate: 750,
    }],
  };
}
